import { useState, useEffect, useCallback, useRef } from 'react'
import SongPreview from './SongPreview'
import SongSearchList from './SongSearchList'

function SetlistEditor({ setlist, onSave, onDiscard, onLabelChange, onPresent }) {
  const [title, setTitle]         = useState(setlist.title || '')
  const [date, setDate]           = useState(setlist.date || '')
  const [songs, setSongs]         = useState(setlist.songs || [])
  const [selectedSong, setSelectedSong] = useState(songs[0] || null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const dragIndexRef              = useRef(null)
  const labelDebounce             = useRef(null)

  useEffect(() => {
    onLabelChange(title || date ? `Setlists - ${title || date}` : 'Setlists - New Setlist')
  }, [title, date, onLabelChange])

  const handleTitleChange = useCallback((e) => {
    const val = e.target.value
    setTitle(val)
    clearTimeout(labelDebounce.current)
    labelDebounce.current = setTimeout(() => {
      onLabelChange(val || date ? `Setlists - ${val || date}` : 'Setlists - New Setlist')
    }, 300)
  }, [date, onLabelChange])

  const handleDateChange = useCallback((e) => {
    setDate(e.target.value)
  }, [])

  const addSong = useCallback((song) => {
    setSongs(prev => [...prev, song])
  }, [])

  const removeSong = useCallback((index) => {
    setSongs(prev => {
      const next = prev.filter((_, i) => i !== index)
      if (selectedSong === prev[index]) setSelectedSong(next[0] || null)
      return next
    })
  }, [selectedSong])

  const handleDragStart = (index) => { dragIndexRef.current = index }
  const handleDragOver  = (e, index) => {
    e.preventDefault()
    setDragOverIndex(index)
  }
  const handleDrop = (e, toIndex) => {
    e.preventDefault()
    const fromIndex = dragIndexRef.current
    if (fromIndex !== null && fromIndex !== toIndex) {
      setSongs(prev => {
        const next = [...prev]
        const [moved] = next.splice(fromIndex, 1)
        next.splice(toIndex, 0, moved)
        return next
      })
    }
    dragIndexRef.current = null
    setDragOverIndex(null)
  }
  const handleDragEnd = () => {
    dragIndexRef.current = null
    setDragOverIndex(null)
  }

  const currentState = { ...setlist, title, date, songs }

  return (
    <div className="setlist-editor">
      {/* Library Column */}
      <div className="setlist-editor-col">
        <div className="setlist-editor-col-label">Search</div>
        <SongSearchList
          onSelect={(song) => setSelectedSong(song)}
          onAdd={(song) => addSong(song)}
        />
      </div>

      {/* Setlist Column */}
      <div className="setlist-editor-col setlist-editor-col--middle">
        <div className="setlist-editor-col-label">Setlist</div>
        <input
          className="form-control mb-3 mx-auto"
          placeholder="Setlist name"
          value={title}
          onChange={handleTitleChange}
        />
        <input
          className="form-control mb-3 mx-auto"
          type="date"
          value={date}
          onChange={handleDateChange}
        />
        <div className="setlist-editor-ordered-list mt-4">
          {songs.map((song, index) => (
            <div
              key={index}
              className={`setlist-editor-song-item frosted ticks${selectedSong === song ? ' setlist-editor-song-item--selected ticks--active' : ''}${dragOverIndex === index && dragIndexRef.current !== index ? ' setlist-editor-song-item--drag-over' : ''}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              onClick={() => setSelectedSong(song)}
            >
              <span className="setlist-editor-drag-handle">⠿</span>
              <div className="setlist-editor-song-title">
                <div className={`fs-6${selectedSong === song ? ' fg-ac1' : ' fg-l2'}`}>{song.title}</div>
              </div>
              <button
                className="setlist-editor-delete-btn"
                onClick={e => { e.stopPropagation(); removeSong(index) }}
              >✕</button>
            </div>
          ))}
          <div className="setlist-editor-drop-zone">
            + drop here to add
          </div>
        </div>
        {onPresent && (
          <button
            className="present-btn frosted ticks mt-3"
            onClick={() => onPresent({ ...setlist, title, date, songs })}
          >
            Present
          </button>
        )}
      </div>

      {/* Preview Column */}
      <div className="setlist-editor-col">
        <div className="setlist-editor-col-label">Preview</div>
        <SongPreview song={selectedSong} />
      </div>

      <span data-testid="editor-state" data-state={JSON.stringify(currentState)} style={{display:'none'}} />
    </div>
  )
}

export default SetlistEditor
